import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import SearchableSelect from "./SearchableSelect";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const useOptions = (path, params, enabled) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!enabled) { setItems([]); return; }
    let alive = true;
    setLoading(true);
    api.get(path, { params }).then((r) => { if (alive) setItems(r.data.items); }).catch(() => { if (alive) setItems([]); }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [path, key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return [items, loading];
};

// Cascading Provinsi -> Kabupaten/Kota -> Kecamatan -> Kelurahan/Desa -> Kode Pos (otomatis).
export const WilayahCascade = ({ form, set, setMany, testPrefix = "" }) => {
  const tid = (s) => `${testPrefix}${s}`;
  const [provinsi, lP] = useOptions("/wilayah/provinsi", {}, true);
  const [kab, lK] = useOptions("/wilayah/kabupaten", { provinsi: form.provinsi }, Boolean(form.provinsi));
  const [kec, lC] = useOptions("/wilayah/kecamatan", { provinsi: form.provinsi, kabupaten: form.kota }, Boolean(form.provinsi && form.kota));
  const [kel, lL] = useOptions("/wilayah/kelurahan", { provinsi: form.provinsi, kabupaten: form.kota, kecamatan: form.kecamatan }, Boolean(form.provinsi && form.kota && form.kecamatan));
  const [pos, lZ] = useOptions("/wilayah/kodepos", { provinsi: form.provinsi, kabupaten: form.kota, kecamatan: form.kecamatan, kelurahan: form.kelurahan }, Boolean(form.provinsi && form.kota && form.kecamatan && form.kelurahan));

  useEffect(() => {
    if (pos.length === 1 && form.kode_pos !== pos[0]) set("kode_pos", pos[0]);
  }, [pos]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMulti = (obj) => (setMany ? setMany(obj) : Object.entries(obj).forEach(([k, v]) => set(k, v)));
  const legacy = !form.provinsi && (form.kota || form.kecamatan || form.kelurahan);

  return (
    <div className="space-y-4">
      {legacy && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="wilayah-legacy-hint">Alamat lama: {[form.kelurahan, form.kecamatan, form.kota].filter(Boolean).join(", ")}. Pilih Provinsi untuk melengkapi wilayah dengan master data.</p>}
      <F label="Provinsi" loading={lP}>
        <SearchableSelect options={provinsi} value={form.provinsi} onChange={(v) => setMulti({ provinsi: v, kota: "", kecamatan: "", kelurahan: "", kode_pos: "" })} placeholder="Cari provinsi..." testId={tid("select-provinsi")} />
      </F>
      <F label="Kabupaten/Kota" loading={lK}>
        <SearchableSelect options={kab} value={form.provinsi ? form.kota : ""} onChange={(v) => setMulti({ kota: v, kecamatan: "", kelurahan: "", kode_pos: "" })} placeholder={form.provinsi ? "Cari kabupaten/kota..." : "Pilih provinsi terlebih dahulu"} testId={tid("select-kota")} className={!form.provinsi ? "opacity-60 pointer-events-none" : ""} />
      </F>
      <F label="Kecamatan" loading={lC}>
        <SearchableSelect options={kec} value={form.provinsi ? form.kecamatan : ""} onChange={(v) => setMulti({ kecamatan: v, kelurahan: "", kode_pos: "" })} placeholder={form.kota ? "Cari kecamatan..." : "Pilih kabupaten/kota terlebih dahulu"} testId={tid("select-kecamatan")} className={!form.kota || !form.provinsi ? "opacity-60 pointer-events-none" : ""} />
      </F>
      <F label="Kelurahan/Desa" loading={lL}>
        <SearchableSelect options={kel} value={form.provinsi ? form.kelurahan : ""} onChange={(v) => setMulti({ kelurahan: v, kode_pos: "" })} placeholder={form.kecamatan ? "Cari kelurahan/desa..." : "Pilih kecamatan terlebih dahulu"} testId={tid("select-kelurahan")} className={!form.kecamatan || !form.provinsi ? "opacity-60 pointer-events-none" : ""} />
      </F>
      <F label="Kode Pos" loading={lZ}>
        {pos.length > 1 ? (
          <SearchableSelect options={pos} value={form.kode_pos} onChange={(v) => set("kode_pos", v)} placeholder="Pilih kode pos (lebih dari satu tersedia)" testId={tid("select-kode-pos")} allowClear={false} />
        ) : (
          <Input inputMode="numeric" maxLength={5} value={form.kode_pos} readOnly={pos.length === 1} onChange={(e) => set("kode_pos", e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder={form.kelurahan ? "Otomatis dari wilayah" : "5 digit angka"} className={`min-h-[44px] rounded-xl font-mono ${pos.length === 1 ? "bg-slate-50" : ""}`} data-testid={tid("input-kode-pos")} />
        )}
        {pos.length === 1 && <p className="text-xs text-slate-500 mt-1" data-testid={tid("kode-pos-auto-hint")}>Kode pos terisi otomatis dari master wilayah.</p>}
      </F>
    </div>
  );
};

const F = ({ label, loading, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label} {loading && <span className="text-xs text-slate-400 font-normal">memuat...</span>}</Label>
    {children}
  </div>
);

export default WilayahCascade;
